/**
 * Actions that must produce an `audit_logs` row. The vocabulary lives here so
 * every phase writes the same strings and the audit screen can filter on them.
 */
export const AUDIT_ACTIONS = {
  COMPETITION_CREATE: 'competition.create',
  COMPETITION_UPDATE: 'competition.update',
  COMPETITION_DEADLINE_CHANGE: 'competition.deadline_change',
  COMPETITION_STATUS_CHANGE: 'competition.status_change',
  COMPETITION_SETTINGS_UPDATE: 'competition.settings_update',
  CATEGORY_CREATE: 'category.create',
  CATEGORY_UPDATE: 'category.update',
  CATEGORY_ARCHIVE: 'category.archive',
  ROUND_CREATE: 'round.create',
  ROUND_UPDATE: 'round.update',
  ROUND_ADVANCEMENT: 'round.advancement',
  /** An organizer defined, changed or removed a manual input field of a round. */
  ROUND_MANUAL_FIELD_CREATE: 'round.manual_field_create',
  ROUND_MANUAL_FIELD_UPDATE: 'round.manual_field_update',
  ROUND_MANUAL_FIELD_DELETE: 'round.manual_field_delete',
  /** Staff entered or changed a manual score value; the audit entry carries the old and new value. */
  MANUAL_SCORE_ENTER: 'score.manual_enter',
  FORM_CREATE: 'form.create',
  FORM_UPDATE: 'form.update',
  FORM_ARCHIVE: 'form.archive',
  FORM_FIELD_CREATE: 'form_field.create',
  FORM_FIELD_UPDATE: 'form_field.update',
  FORM_FIELD_DELETE: 'form_field.delete',
  FORM_FIELD_REORDER: 'form_field.reorder',
  FORM_VERSION_CLONE: 'form_version.clone',
  FORM_VERSION_PUBLISH: 'form_version.publish',
  ROLE_GRANT: 'role.grant',
  ROLE_REVOKE: 'role.revoke',
  TEAM_CHANGE: 'team.change',
  TEAM_MEMBER_REMOVE: 'team_member.remove',
  TEAM_LEADER_TRANSFER: 'team.leader_transfer',
  TEAM_INVITE_CREATE: 'team_invite.create',
  TEAM_INVITE_RESEND: 'team_invite.resend',
  TEAM_INVITE_REVOKE: 'team_invite.revoke',
  TEAM_INVITE_ACCEPT: 'team_invite.accept',
  /** SDD v1.2 §36 Wave G (P2) "reminder bulk actions" — one entry per send, summarizing the batch. */
  TEAM_REGISTRATION_REMINDER_SEND: 'team.registration_reminder_send',
  /** AC-367: one entry per send of "your saved registration needs an update" emails, summarizing the batch. */
  REGISTRATION_NEEDS_UPDATE_REMINDER_SEND: 'registration.needs_update_reminder_send',
  /** AC-369: the organizer sent themself a test copy of that email (no student is contacted). */
  REGISTRATION_NEEDS_UPDATE_REMINDER_TEST_SEND: 'registration.needs_update_reminder_test_send',
  SUBMISSION_CREATE: 'submission.create',
  SUBMISSION_UPDATE: 'submission.update',
  SUBMISSION_SUBMIT: 'submission.submit',
  SUBMISSION_LOCK: 'submission.lock',
  SUBMISSION_UNLOCK: 'submission.unlock',
  SUBMISSION_WITHDRAW: 'submission.withdraw',
  SUBMISSION_DISQUALIFY: 'submission.disqualify',
  SUBMISSION_REINSTATE: 'submission.reinstate',
  SUBMISSION_RETURN_FOR_CORRECTION: 'submission.return_for_correction',
  FILE_UPLOAD_COMPLETE: 'file.upload_complete',
  /** PR-276 (Submission Section Grouping and Upload Status Spec Delta, 2026-08-30). A genuine upload failure, distinct from a caller-initiated FILE_DELETE/abort. */
  FILE_UPLOAD_FAILED: 'file.upload_failed',
  FILE_DELETE: 'file.delete',
  /** One entry per send of "you still have scoring to do" emails to judges, summarizing the batch. */
  JUDGE_REMINDER_SEND: 'judge.reminder_send',
  /** An organizer turned the automatic judging-deadline reminder on or off, or changed its lead time. */
  JUDGE_AUTO_REMINDER_UPDATE: 'judge.auto_reminder_update',
  /** A judge withdrew from judging a competition; their still-active assignments were cancelled. */
  JUDGE_WITHDRAWAL: 'judge.withdrawal',
  /** A judge confirmed the confidentiality and conflict-of-interest terms for a competition. */
  JUDGE_ACKNOWLEDGEMENT: 'judge.acknowledgement',
  JUDGE_ASSIGNMENT: 'judge.assignment',
  JUDGE_REASSIGNMENT: 'judge.reassignment',
  SCORE_SUBMIT: 'score.submit',
  SCORE_REOPEN: 'score.reopen',
  SCORE_CHANGE: 'score.change',
  RESULT_CALCULATE: 'result.calculate',
  RESULT_LOCK: 'result.lock',
  RESULT_PUBLISH: 'result.publish',
  RESULT_OVERRIDE: 'result.override',
  ANNOUNCEMENT_PUBLISH: 'announcement.publish',
  ANNOUNCEMENT_UPDATE: 'announcement.update',
  ANNOUNCEMENT_DELETE: 'announcement.delete',
  /** One summary entry per email send of an announcement (recipient count and user ids, never addresses). */
  ANNOUNCEMENT_EMAIL_SEND: 'announcement.email_send',
  /** AC-370: the organizer sent themself a test copy of an announcement email (no recipient is contacted). */
  ANNOUNCEMENT_EMAIL_TEST_SEND: 'announcement.email_test_send',
  /** AC-372: how many notification emails an organizer action sent and how many failed (counts and user ids, never addresses). */
  NOTIFICATION_EMAIL_DELIVERY: 'notification.email_delivery',
  AUTH_LOGIN: 'auth.login',
  AUTH_LOGOUT: 'auth.logout',
  AUTH_LOGIN_LOCKED: 'auth.login_locked',
  AUTH_REGISTER: 'auth.register',
  AUTH_PASSWORD_RESET: 'auth.password_reset',
  /** GOV-016, GOV-017/018: the one-time Initial Super Admin Bootstrap grant. Actor and target are always the same user. */
  INITIAL_SUPER_ADMIN_BOOTSTRAP: 'auth.initial_super_admin_bootstrap',
  /** Wave L, GOV-016, BR-062, PR-018/022. */
  USER_SUSPEND: 'user.suspend',
  USER_REACTIVATE: 'user.reactivate',
  /** Wave L, GOV-016, PR-018/022, API-034. Never alters audit/session history rows, only revokes them. */
  USER_SESSIONS_REVOKE: 'user.sessions_revoke',
  /** Wave L, GOV-015/016, PR-019, BR-064/068, API-035/036. */
  SUPER_ADMIN_GRANT: 'super_admin.grant',
  SUPER_ADMIN_REVOKE: 'super_admin.revoke',
  /** Wave P/R, PR-032..038, BR-080/081/085. Never carries the temp password itself — see `AUDIT_REDACTED_KEYS`. */
  JUDGE_INVITATION_CREATE: 'judge_invitation.create',
  JUDGE_INVITATION_CANCEL: 'judge_invitation.cancel',
  /** The invited person declined a judge invitation (with an optional reason). */
  JUDGE_INVITATION_DECLINE: 'judge_invitation.decline',
  JUDGE_INVITATION_RESEND: 'judge_invitation.resend',
  /** Wave R, API-051, BR-083, SEC-023: the forced first-login password change that ends a restricted session. */
  AUTH_FORCED_PASSWORD_CHANGE_COMPLETE: 'auth.forced_password_change_complete',
  /** Wave O, GOV-019/020, PR-025..027. */
  COMPETITION_REQUEST_SUBMIT: 'competition_request.submit',
  COMPETITION_REQUEST_CANCEL: 'competition_request.cancel',
  COMPETITION_REQUEST_REQUEST_CHANGES: 'competition_request.request_changes',
  COMPETITION_REQUEST_REJECT: 'competition_request.reject',
  /** The Automated Provisioning decision itself — see `COMPETITION_CREATE`/`ROLE_GRANT` for the resulting competition/grant entries written in the same batch. */
  COMPETITION_REQUEST_APPROVE: 'competition_request.approve',
  /** Wave T, PR-041, BR-087: self-service `PATCH /me/profile`. Never includes role/status/permission fields — those cannot appear, see GOV-026. */
  PROFILE_SELF_UPDATE: 'profile.self_update',
  /** Wave T, PR-042, BR-088, SEC-026: self-service `POST /me/password`. Distinct from `AUTH_PASSWORD_RESET` (unauthenticated, token-based). */
  AUTH_PASSWORD_CHANGE: 'auth.password_change',
  /** PR-043, SEC-027: `POST /me/email` created a pending change. Never carries the raw token or its hash. */
  AUTH_EMAIL_CHANGE_REQUESTED: 'auth.email_change_requested',
  /** PR-043, BR-091, API-058, AC-059: the authoritative `users.email` switch, after ownership verification. */
  AUTH_EMAIL_CHANGE_VERIFIED: 'auth.email_change_verified',
  /** Wave T, PR-045, BR-092, API-060: self-service session revoke — a caller can only ever target their own session(s), unlike `USER_SESSIONS_REVOKE`. */
  SESSION_SELF_REVOKE: 'session.self_revoke',
  /** PR-130: Platform Admin creates/edits the platform-wide Discovery Category taxonomy. */
  PLATFORM_DISCOVERY_CATEGORY_CREATE: 'platform_discovery_category.create',
  PLATFORM_DISCOVERY_CATEGORY_UPDATE: 'platform_discovery_category.update',
  /**
   * Member Student Eligibility & Enrollment Proof, Phase 1 (SDD §11 Audit
   * Contract). Payloads carry `competitionId`/`targetUserId`/`proofId`/
   * `proofType`/`decision`/`reason` only — never proof bytes, a signed URL,
   * or object storage secrets (enforced by keeping those keys out of the
   * `before`/`after` objects passed to `AuditService`, not by
   * `AUDIT_REDACTED_KEYS`, since none of them are ever put there to begin
   * with).
   */
  ELIGIBILITY_POLICY_UPDATE: 'eligibility_policy.update',
  ELIGIBILITY_PROOF_UPLOAD_COMPLETE: 'eligibility_proof.upload_complete',
  ELIGIBILITY_PROOF_REPLACE: 'eligibility_proof.replace',
  ELIGIBILITY_VERIFICATION_APPROVED: 'eligibility_verification.approved',
  ELIGIBILITY_VERIFICATION_REJECTED: 'eligibility_verification.rejected',
  /**
   * PRIV-011 (v3.86 Phase 4 retention sweep). Always system-attributed
   * (`actorUserId: null`), the same convention `FILE_DELETE` already uses
   * for `cleanupExpiredUploads` — an automated action has no human operator
   * to attribute it to. Never requires an operator-supplied reason (see
   * `AUDIT_ACTIONS_REQUIRING_REASON` below); the sweep always writes the
   * same fixed, self-explanatory reason string.
   */
  ELIGIBILITY_PROOF_RETENTION_DELETE: 'eligibility_proof.retention_delete',
  /**
   * PRIV-012/013, API-098..101 (v3.98): Account Erasure. `REQUESTED`/
   * `CANCELLED` are self-service, attributed to the requester; `REJECTED`/
   * `EXECUTED` are Platform Admin actions. `EXECUTED` is the one that
   * actually anonymizes the account and hard-deletes its auth credentials —
   * see `apps/api/src/services/account-erasure-service.ts`.
   */
  ACCOUNT_ERASURE_REQUESTED: 'account_erasure.requested',
  ACCOUNT_ERASURE_CANCELLED: 'account_erasure.cancelled',
  ACCOUNT_ERASURE_REJECTED: 'account_erasure.rejected',
  ACCOUNT_ERASURE_EXECUTED: 'account_erasure.executed',
  /** API-098: read-only, but logged for accountability — a full personal-data dossier was downloaded. */
  DATA_EXPORT_DOWNLOADED: 'data_export.downloaded',
  /**
   * Export Center (SDD v1.1 §4.3.7/§16.4/§21): every call to
   * `GET /:competitionId/admin/reports/:report.csv` writes one of these,
   * closing a pre-existing gap — the `teams`/`submissions` reports this
   * endpoint already served had NO audit trail at all before this. Payload
   * (`after`) carries `reportKind`/`filters`/`fields`/`rowCount` only —
   * never the exported row data itself. Distinct from `DATA_EXPORT_DOWNLOADED`
   * (PRIV-012, a caller's own personal-data dossier) — this is a
   * competition-scoped operator report, not a `/me/*` self-service export.
   */
  ADMIN_REPORT_EXPORT: 'admin_report.export',
  /**
   * v4.46 Competition Configuration JSON-only. Payload (`after`) carries
   * `schemaVersion`/`exportedAt` only per Autonomous Execution Spec E-05 —
   * never the exported JSON body itself, and never routed through
   * `AUDIT_REDACTED_KEYS` since the full body is never placed there to
   * begin with (same convention `ELIGIBILITY_POLICY_UPDATE` documents).
   */
  COMPETITION_CONFIG_EXPORT: 'competition_config.export',
  /** Wave C. `entityId` is the newly created competition's id; `before` carries the source competition's id, never its full configuration. */
  COMPETITION_CLONE: 'competition.clone',
  /** Wave D. Payload carries template id/name/schemaVersion/visibility only, never the configuration body. */
  TEMPLATE_CREATE: 'competition_template.create',
  TEMPLATE_REVISION_CREATE: 'competition_template.revision_create',
  TEMPLATE_CREATE_COMPETITION: 'competition_template.create_competition',
  /** Wave F. `IMPORT_CREATE` is the only import action audited; validate-only calls are not (Autonomous Execution Spec G-06). */
  CONFIG_IMPORT_CREATE: 'competition_config.import_create',
} as const

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS]

/**
 * Actions that are only meaningful with an operator-supplied reason. The
 * handler rejects the request when one is missing rather than writing an entry
 * that records what happened but not why.
 */
export const AUDIT_ACTIONS_REQUIRING_REASON: readonly AuditAction[] = [
  AUDIT_ACTIONS.COMPETITION_STATUS_CHANGE,
  AUDIT_ACTIONS.SUBMISSION_LOCK,
  AUDIT_ACTIONS.SUBMISSION_UNLOCK,
  AUDIT_ACTIONS.SUBMISSION_WITHDRAW,
  AUDIT_ACTIONS.SUBMISSION_DISQUALIFY,
  AUDIT_ACTIONS.SUBMISSION_REINSTATE,
  AUDIT_ACTIONS.SUBMISSION_RETURN_FOR_CORRECTION,
  AUDIT_ACTIONS.SCORE_REOPEN,
  AUDIT_ACTIONS.SCORE_CHANGE,
  AUDIT_ACTIONS.FILE_DELETE,
  AUDIT_ACTIONS.RESULT_OVERRIDE,
  AUDIT_ACTIONS.USER_SUSPEND,
  AUDIT_ACTIONS.USER_SESSIONS_REVOKE,
  AUDIT_ACTIONS.SUPER_ADMIN_GRANT,
  AUDIT_ACTIONS.SUPER_ADMIN_REVOKE,
  AUDIT_ACTIONS.ROLE_GRANT,
  AUDIT_ACTIONS.ROLE_REVOKE,
  AUDIT_ACTIONS.COMPETITION_REQUEST_REQUEST_CHANGES,
  AUDIT_ACTIONS.COMPETITION_REQUEST_REJECT,
  AUDIT_ACTIONS.COMPETITION_REQUEST_APPROVE,
  AUDIT_ACTIONS.ELIGIBILITY_VERIFICATION_REJECTED,
  AUDIT_ACTIONS.ACCOUNT_ERASURE_REJECTED,
  AUDIT_ACTIONS.ACCOUNT_ERASURE_EXECUTED,
]

/** Entity types referenced by `audit_logs.entity_type`. */
export const AUDIT_ENTITY_TYPES = {
  COMPETITION: 'competition',
  COMPETITION_SETTINGS: 'competition_settings',
  COMPETITION_CATEGORY: 'competition_category',
  COMPETITION_ROLE: 'competition_role',
  ROUND: 'round',
  ROUND_ADVANCEMENT: 'round_advancement',
  ROUND_MANUAL_FIELD: 'round_manual_field',
  ROUND_MANUAL_ENTRY: 'round_manual_entry',
  FORM: 'form',
  FORM_VERSION: 'form_version',
  FORM_FIELD: 'form_field',
  TEAM: 'team',
  TEAM_MEMBER: 'team_member',
  TEAM_INVITE: 'team_invite',
  SUBMISSION: 'submission',
  SUBMISSION_FILE: 'submission_file',
  JUDGE_ASSIGNMENT: 'judge_assignment',
  SCORE: 'score',
  RESULT_RUN: 'result_run',
  ANNOUNCEMENT: 'announcement',
  SESSION: 'session',
  USER: 'user',
  JUDGE_INVITATION: 'judge_invitation',
  /** Wave O, GOV-019/020. */
  COMPETITION_REQUEST: 'competition_request',
  /** PR-130. Platform-scoped, unlike `COMPETITION_CATEGORY`. */
  DISCOVERY_CATEGORY: 'discovery_category',
  /** Member Student Eligibility & Enrollment Proof, Phase 1. */
  ELIGIBILITY_POLICY: 'eligibility_policy',
  MEMBER_ELIGIBILITY: 'member_eligibility',
  ENROLLMENT_PROOF: 'enrollment_proof',
  /** PRIV-012/013 (v3.98). `entityId` is the request id; `ACCOUNT_ERASURE_EXECUTED` itself uses `USER` (entityId = the erased user). */
  ACCOUNT_ERASURE_REQUEST: 'account_erasure_request',
  /** Export Center. `entityId` is the report kind (`teams`/`submissions`/`participants`/`eligibility`/`registration`/`judging`) — lets the history endpoint filter by kind via the existing `entityId` query param, no repository change needed. */
  ADMIN_REPORT: 'admin_report',
  /** v4.46 Competition Configuration JSON-only, Wave D. */
  COMPETITION_TEMPLATE: 'competition_template',
  COMPETITION_TEMPLATE_REVISION: 'competition_template_revision',
} as const

export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[keyof typeof AUDIT_ENTITY_TYPES]

/**
 * Keys never written into `before_json` / `after_json`, whatever object is
 * handed to the audit writer. An audit trail that records a password hash or a
 * session token turns a read of the trail into an escalation.
 */
export const AUDIT_REDACTED_KEYS: readonly string[] = [
  'password',
  'passwordHash',
  'password_hash',
  'token',
  'tokenHash',
  'token_hash',
  'csrfToken',
  'csrfTokenHash',
  'csrf_token_hash',
  'secret',
  'sessionHashSecret',
  'clientSecret',
]

/**
 * UX-108 #3 — the competition audit page's event-category filter. Only maps
 * actions that can ever carry a `competitionId` (competition-scoped, per
 * `AuditRecordInput`); platform-only actions (`auth.login`, `user.suspend`,
 * ...) never appear on that screen and are left uncategorized rather than
 * given a category they cannot occupy there.
 *
 * Lives in `@napaicp/shared` (not `apps/web`) so both the audit-log query
 * filter (`AuditLogsRepository.listByCompetition`, server-side) and the audit
 * page's display chips (`apps/web/src/lib/audit-activity.ts`) derive from one
 * mapping — a second, independent copy is what let the category filter drift
 * to client-side-only in the first place (see the SDD gap audit that closed
 * this).
 */
export const AUDIT_CATEGORIES = [
  'competitionSetup',
  'peopleAndRoles',
  'team',
  'submission',
  'file',
  'judging',
  'scoring',
  'result',
  'form',
] as const

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number]

const ACTION_CATEGORIES: Partial<Record<AuditAction, AuditCategory>> = {
  [AUDIT_ACTIONS.COMPETITION_CREATE]: 'competitionSetup',
  [AUDIT_ACTIONS.COMPETITION_UPDATE]: 'competitionSetup',
  [AUDIT_ACTIONS.COMPETITION_DEADLINE_CHANGE]: 'competitionSetup',
  [AUDIT_ACTIONS.COMPETITION_STATUS_CHANGE]: 'competitionSetup',
  [AUDIT_ACTIONS.COMPETITION_SETTINGS_UPDATE]: 'competitionSetup',
  [AUDIT_ACTIONS.CATEGORY_CREATE]: 'competitionSetup',
  [AUDIT_ACTIONS.CATEGORY_UPDATE]: 'competitionSetup',
  [AUDIT_ACTIONS.CATEGORY_ARCHIVE]: 'competitionSetup',
  [AUDIT_ACTIONS.ROUND_CREATE]: 'competitionSetup',
  [AUDIT_ACTIONS.ROUND_UPDATE]: 'competitionSetup',
  [AUDIT_ACTIONS.ROUND_ADVANCEMENT]: 'competitionSetup',
  [AUDIT_ACTIONS.ROUND_MANUAL_FIELD_CREATE]: 'competitionSetup',
  [AUDIT_ACTIONS.ROUND_MANUAL_FIELD_UPDATE]: 'competitionSetup',
  [AUDIT_ACTIONS.ROUND_MANUAL_FIELD_DELETE]: 'competitionSetup',
  [AUDIT_ACTIONS.MANUAL_SCORE_ENTER]: 'scoring',
  [AUDIT_ACTIONS.FORM_CREATE]: 'form',
  [AUDIT_ACTIONS.FORM_UPDATE]: 'form',
  [AUDIT_ACTIONS.FORM_ARCHIVE]: 'form',
  [AUDIT_ACTIONS.FORM_FIELD_CREATE]: 'form',
  [AUDIT_ACTIONS.FORM_FIELD_UPDATE]: 'form',
  [AUDIT_ACTIONS.FORM_FIELD_DELETE]: 'form',
  [AUDIT_ACTIONS.FORM_FIELD_REORDER]: 'form',
  [AUDIT_ACTIONS.FORM_VERSION_CLONE]: 'form',
  [AUDIT_ACTIONS.FORM_VERSION_PUBLISH]: 'form',
  [AUDIT_ACTIONS.ROLE_GRANT]: 'peopleAndRoles',
  [AUDIT_ACTIONS.ROLE_REVOKE]: 'peopleAndRoles',
  [AUDIT_ACTIONS.TEAM_CHANGE]: 'team',
  [AUDIT_ACTIONS.TEAM_MEMBER_REMOVE]: 'team',
  [AUDIT_ACTIONS.TEAM_LEADER_TRANSFER]: 'team',
  [AUDIT_ACTIONS.TEAM_INVITE_CREATE]: 'team',
  [AUDIT_ACTIONS.TEAM_INVITE_RESEND]: 'team',
  [AUDIT_ACTIONS.TEAM_INVITE_REVOKE]: 'team',
  [AUDIT_ACTIONS.TEAM_INVITE_ACCEPT]: 'team',
  [AUDIT_ACTIONS.TEAM_REGISTRATION_REMINDER_SEND]: 'team',
  [AUDIT_ACTIONS.REGISTRATION_NEEDS_UPDATE_REMINDER_SEND]: 'team',
  [AUDIT_ACTIONS.REGISTRATION_NEEDS_UPDATE_REMINDER_TEST_SEND]: 'team',
  [AUDIT_ACTIONS.SUBMISSION_CREATE]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_UPDATE]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_SUBMIT]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_LOCK]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_UNLOCK]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_WITHDRAW]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_DISQUALIFY]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_REINSTATE]: 'submission',
  [AUDIT_ACTIONS.SUBMISSION_RETURN_FOR_CORRECTION]: 'submission',
  [AUDIT_ACTIONS.FILE_UPLOAD_COMPLETE]: 'file',
  [AUDIT_ACTIONS.FILE_UPLOAD_FAILED]: 'file',
  [AUDIT_ACTIONS.FILE_DELETE]: 'file',
  [AUDIT_ACTIONS.JUDGE_AUTO_REMINDER_UPDATE]: 'judging',
  [AUDIT_ACTIONS.JUDGE_WITHDRAWAL]: 'judging',
  [AUDIT_ACTIONS.JUDGE_ACKNOWLEDGEMENT]: 'judging',
  [AUDIT_ACTIONS.JUDGE_REMINDER_SEND]: 'judging',
  [AUDIT_ACTIONS.JUDGE_ASSIGNMENT]: 'judging',
  [AUDIT_ACTIONS.JUDGE_REASSIGNMENT]: 'judging',
  [AUDIT_ACTIONS.JUDGE_INVITATION_CREATE]: 'judging',
  [AUDIT_ACTIONS.JUDGE_INVITATION_CANCEL]: 'judging',
  [AUDIT_ACTIONS.JUDGE_INVITATION_DECLINE]: 'judging',
  [AUDIT_ACTIONS.JUDGE_INVITATION_RESEND]: 'judging',
  [AUDIT_ACTIONS.SCORE_SUBMIT]: 'scoring',
  [AUDIT_ACTIONS.SCORE_REOPEN]: 'scoring',
  [AUDIT_ACTIONS.SCORE_CHANGE]: 'scoring',
  [AUDIT_ACTIONS.RESULT_CALCULATE]: 'result',
  [AUDIT_ACTIONS.RESULT_LOCK]: 'result',
  [AUDIT_ACTIONS.RESULT_PUBLISH]: 'result',
  [AUDIT_ACTIONS.RESULT_OVERRIDE]: 'result',
  [AUDIT_ACTIONS.ANNOUNCEMENT_PUBLISH]: 'result',
  [AUDIT_ACTIONS.ANNOUNCEMENT_UPDATE]: 'result',
  [AUDIT_ACTIONS.ANNOUNCEMENT_DELETE]: 'result',
  [AUDIT_ACTIONS.ANNOUNCEMENT_EMAIL_SEND]: 'result',
  [AUDIT_ACTIONS.ANNOUNCEMENT_EMAIL_TEST_SEND]: 'result',
  [AUDIT_ACTIONS.NOTIFICATION_EMAIL_DELIVERY]: 'result',
}
// Note: ACCOUNT_ERASURE_* / DATA_EXPORT_DOWNLOADED are deliberately absent
// here, same as USER_SUSPEND/PROFILE_SELF_UPDATE/AUTH_EMAIL_CHANGE_* above —
// this map only categorizes competition-scoped audit actions for the
// Competition Audit page's category picker; platform/self-service account
// actions are not shown there and have no category.

export function categoryForAction(action: string): AuditCategory | null {
  return ACTION_CATEGORIES[action as AuditAction] ?? null
}

/**
 * Every raw action string that falls under one category — used both to build
 * the audit page's "進階：事件代碼" picker and, server-side, to translate a
 * `category` query param into an `action IN (...)` SQL clause
 * (`AuditLogsRepository.listByCompetition`).
 */
export function actionsInCategory(category: AuditCategory): string[] {
  return Object.entries(ACTION_CATEGORIES)
    .filter(([, value]) => value === category)
    .map(([action]) => action)
}
